import { Point } from "@/routes/Frontend/FuturebilityCockpit/types";
import { PersonalityMatrixAdvancedContent } from "./types";

const toScreenCoordinates = (x: number, y: number) => ({
  x,
  y: 1 - y,
});

const hexToRgb = (hex: string) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

export const interpolateColor = (
  percentage: number,
  minPercentage: number,
  maxPercentage: number
): string => {
  // Interpolate between #302154 (lowest) and #423464 (alt #958da8) (highest)
  const minColor = hexToRgb("#302154");
  const maxColor = hexToRgb("#423464");

  // Normalize percentage between 0 and 1
  const range = maxPercentage - minPercentage;
  const normalized = range === 0 ? 0 : (percentage - minPercentage) / range;

  const r = Math.round(minColor.r + (maxColor.r - minColor.r) * normalized);
  const g = Math.round(minColor.g + (maxColor.g - minColor.g) * normalized);
  const b = Math.round(minColor.b + (maxColor.b - minColor.b) * normalized);

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

export const getPosition = (
  x: number,
  y: number
): { left: string; top: string } => {
  const screen = toScreenCoordinates(x, y);

  return {
    left: `${screen.x * 100}%`,
    top: `${screen.y * 100}%`,
  };
};

export const calculateInGrid = {
  topLeft: [
    { x: 0.5, y: 1 },
    { x: 0.5, y: 0.5 },
    { x: 0, y: 0.5 },
    { x: 0, y: 1 },
  ],
  topRight: [
    { x: 1, y: 1 },
    { x: 1, y: 0.5 },
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 1 },
  ],
  bottomLeft: [
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0.5 },
  ],
  bottomRight: [
    { x: 1, y: 0.5 },
    { x: 1, y: 0 },
    { x: 0.5, y: 0 },
    { x: 0.5, y: 0.5 },
  ],
  left: [
    { x: 0, y: 0 },
    { x: 0.5, y: 0 },
    { x: 0, y: 1 },
    { x: 0.5, y: 1 },
  ],
  right: [
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0.5, y: 1 },
    { x: 0.5, y: 0 },
  ],
  top: [
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 1, y: 0.5 },
    { x: 0, y: 0.5 },
  ],
  bottom: [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 0.5 },
    { x: 0, y: 0.5 },
  ],
  grid1: [
    { x: 0, y: 1 },
    { x: 1 / 3, y: 1 },
    { x: 1 / 3, y: 2 / 3 },
    { x: 0, y: 2 / 3 },
  ],
  grid2: [
    { x: 1 / 3, y: 1 },
    { x: 2 / 3, y: 1 },
    { x: 2 / 3, y: 2 / 3 },
    { x: 1 / 3, y: 2 / 3 },
  ],
  grid3: [
    { x: 2 / 3, y: 1 },
    { x: 1, y: 1 },
    { x: 1, y: 2 / 3 },
    { x: 2 / 3, y: 2 / 3 },
  ],
  grid4: [
    { x: 0, y: 2 / 3 },
    { x: 1 / 3, y: 2 / 3 },
    { x: 1 / 3, y: 1 / 3 },
    { x: 0, y: 1 / 3 },
  ],
  grid5: [
    { x: 1 / 3, y: 2 / 3 },
    { x: 2 / 3, y: 2 / 3 },
    { x: 2 / 3, y: 1 / 3 },
    { x: 1 / 3, y: 1 / 3 },
  ],
  grid6: [
    { x: 2 / 3, y: 2 / 3 },
    { x: 1, y: 2 / 3 },
    { x: 1, y: 1 / 3 },
    { x: 2 / 3, y: 1 / 3 },
  ],
  grid7: [
    { x: 0, y: 1 / 3 },
    { x: 1 / 3, y: 1 / 3 },
    { x: 1 / 3, y: 0 },
    { x: 0, y: 0 },
  ],
  grid8: [
    { x: 1 / 3, y: 1 / 3 },
    { x: 2 / 3, y: 1 / 3 },
    { x: 2 / 3, y: 0 },
    { x: 1 / 3, y: 0 },
  ],
  grid9: [
    { x: 2 / 3, y: 1 / 3 },
    { x: 1, y: 1 / 3 },
    { x: 1, y: 0 },
    { x: 2 / 3, y: 0 },
  ],
};

export const getQuadrantPercentage = (
  quadrantType: keyof PersonalityMatrixAdvancedContent,
  points: Point[]
): number => {
  const quadrant = calculateInGrid[quadrantType];

  // Get boundaries from the quadrant points
  const xCoords = quadrant.map(p => p.x).sort((a, b) => a - b);
  const yCoords = quadrant.map(p => p.y).sort((a, b) => a - b);

  const left = xCoords[0];
  const right = xCoords[xCoords.length - 1];
  const bottom = yCoords[0];
  const top = yCoords[yCoords.length - 1];

  // Filter points within the boundaries
  const pointsInQuadrant = points.filter(
    point =>
      point.x >= left && point.x <= right && point.y >= bottom && point.y <= top
  );

  const count = pointsInQuadrant.length;
  const percentage = points.length > 0 ? (count / points.length) * 100 : 0;

  return Math.round(percentage * 100) / 100;
};

export const getPercentagesByQuadrant = (points: Point[]) => {
  return (
    Object.keys(calculateInGrid) as Array<
      keyof PersonalityMatrixAdvancedContent
    >
  ).reduce((acc, key) => {
    acc[key] = getQuadrantPercentage(key, points);
    return acc;
  }, {} as Record<keyof PersonalityMatrixAdvancedContent, number>);
};
